import filter from 'lodash/filter';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';
import { translate } from 'helpers/translateHelper';
import { opButtons, multiChartOptions } from './constants';
import { Trend } from './SingleChartView/Trend';
import { TrendCharts } from './MultiChartView/TrendCharts';
import { MAX_TREND_SIGNALS } from '../constants';
import { setColor } from '../commonTrendHelpers';
import { useNewCapability, useUserCapability } from 'sharedHooks';
import { VibrationFFTV2 } from './VibrationFFTV2';
import { HistogramV2 } from './SingleChartView/HistogramV2';
import { ScatterPlotV2 } from './SingleChartView/ScatterPlotV2';

const getTabsVisibility = () => {
    const capability = useNewCapability();
    const userCapabilities = useUserCapability();
    return {
        [opButtons.trends]: capability.hasTrendCapability(),
        // [opButtons.scatterPlot]: capability.hasScatterCapability() && userCapabilities.hasScatterLegacyUserCapability(),
        [opButtons.scatterPlotV2]: capability.hasScatterCapability() && userCapabilities.hasScatterV2UserCapability(),
        // [opButtons.histogram]: capability.hasHistogramCapability() && userCapabilities.hasHistogramLegacyUserCapability(),
        [opButtons.histogramV2]: capability.hasHistogramCapability() && userCapabilities.hasHistogramV2UserCapability(),
        // [opButtons.vibrationFFT]: capability.hasVibrationFFTCapability() && userCapabilities.hasVibrationFFTLegacyUserCapability() && capability.isSmartSensor(),
        [opButtons.vibrationFFTV2]: capability.hasVibrationFFTCapability() && userCapabilities.hasVibrationFFTV2UserCapability() && capability.isSmartSensor(),
    };
};

export const getToggleOptions = (multiChart, isUnmonitored) => {
    const allTabs = [{
        label: translate('ABB.Powertrain.Frontend.opTrendsTabBtn'),
        value: opButtons.trends,
        component: multiChart === multiChartOptions.SINGLE ? Trend : TrendCharts
    },
    {
        label: translate('ABB.Powertrain.Frontend.opScatterPlotTabBtn'),
        value: opButtons.scatterPlotV2,
        component: ScatterPlotV2
    },
    {
        label: translate('ABB.Powertrain.Frontend.opHistogramTabBtn'),
        value: opButtons.histogramV2,
        component: HistogramV2,
    },
    {
        label: translate('ABB.Powertrain.Frontend.opVibrationFFTTabBtn'),
        value: opButtons.vibrationFFTV2,
        component: VibrationFFTV2
    }];
    const tabVisibility = getTabsVisibility();

    return filter(allTabs, (tab) => {
        if (isUnmonitored) {
            return tab.value === opButtons.trends;
        }
        return tab.value !== opButtons.vibrationFFT && tabVisibility[tab.value];
    });
};

const hasKpi = (kpiList, kpi) => {
    const flatKPIList = flatMap(kpiList, (kpiGroup) => kpiGroup.timeseries);

    return !!find(flatKPIList, (kpifomList) => {
        return kpifomList.timeseriesKey === kpi;
    });
};

export const isAssetCompatibleWithSelectedKPIs = (asset, selectedKPIs) => {
    return !!selectedKPIs.length && selectedKPIs[0]?.assetTypeID === asset?.componentType?.typeID;
};

export const isAssetCompatibleWithSelectedTrendKPIs = (asset, assetTypeID) => {
    return !!assetTypeID && assetTypeID === asset?.assetType;
};

export const getCommonTrendKPIs = (newTrendKPIs, selectedTrendKPIs) => {
    const commonTrendKPIs = [];

    for (const selectedKPI of selectedTrendKPIs) {
        hasKpi(newTrendKPIs, selectedKPI) && commonTrendKPIs.push(selectedKPI);
    }
    return commonTrendKPIs;
};

// export const getAvailableKPI = (newKPIs, selectedKPI) => {
//     return find(newKPIs, newKPI => newKPI.timeseriesKey === selectedKPI?.timeseriesKey) ? selectedKPI : null;
// };


export const getAvailableKPIV2 = (newKPIs, selectedKPI, isFromScatter) => {
    return find(newKPIs, newKPI => isFromScatter ?
        newKPI.scatterKey === selectedKPI?.scatterKey
        : newKPI.histogramKey === selectedKPI?.histogramKey
    ) ? selectedKPI : null;
};

export const addKPISelection = (selectedKPIs, newKPI) => {
    // Ignore duplicate selections to keep tab counters stable across repeated navigations.
    if (selectedKPIs?.includes(newKPI)) {
        return selectedKPIs;
    }
    selectedKPIs.push(newKPI);
    if (selectedKPIs.length > MAX_TREND_SIGNALS) {
        selectedKPIs.shift();
    }
    return selectedKPIs;
};

export const addSelectOpTrendKPIData = (selectedTrendsData, newSelectedTrendKpiArray, validKpiKeys = null) => {

    if (!newSelectedTrendKpiArray?.length) {
        return selectedTrendsData;
    }

    if (validKpiKeys) {
        selectedTrendsData = selectedTrendsData?.filter(item =>
            validKpiKeys?.includes(item?.kpiIdentifier)
        );
    }

    if (!selectedTrendsData?.length && newSelectedTrendKpiArray?.length > 1) {
        newSelectedTrendKpiArray?.forEach((newSelectedTrend) => {
            selectedTrendsData?.push({
                kpiIdentifier: newSelectedTrend?.config?.timeseriesKey,
                values: newSelectedTrend?.values,
                minValue: newSelectedTrend?.minValue,
                maxValue: newSelectedTrend?.maxValue,
                avgValue: newSelectedTrend?.avgValue,
                offset: newSelectedTrend?.offset,
                precision: newSelectedTrend?.config?.timeseriesPrecision
            });
        });
        return setColor(selectedTrendsData);
    }

    // Add signals from the API response that aren't already in selectedTrendsData.
    newSelectedTrendKpiArray?.forEach((newSelectedTrend) => {
        if (!find(selectedTrendsData, (selectedTrend) => selectedTrend?.kpiIdentifier === newSelectedTrend?.config?.timeseriesKey)) {
            selectedTrendsData.push({
                kpiIdentifier: newSelectedTrend?.config?.timeseriesKey,
                values: newSelectedTrend?.values,
                minValue: newSelectedTrend?.minValue,
                maxValue: newSelectedTrend?.maxValue,
                avgValue: newSelectedTrend?.avgValue,
                offset: newSelectedTrend?.offset,
                precision: newSelectedTrend?.config?.timeseriesPrecision
            });
        }
    });
    if (selectedTrendsData?.length > MAX_TREND_SIGNALS) {
        selectedTrendsData?.splice(0, selectedTrendsData?.length - MAX_TREND_SIGNALS);
    }

    selectedTrendsData = map(selectedTrendsData, (selectedTrend) => {
        const found = find(newSelectedTrendKpiArray, (newSelected) => newSelected?.config?.timeseriesKey === selectedTrend?.kpiIdentifier);
        if (found) {
            return {
                color: selectedTrend.color,
                kpiIdentifier: found?.config?.timeseriesKey,
                values: found?.values,
                minValue: found?.minValue,
                maxValue: found?.maxValue,
                avgValue: found?.avgValue,
                offset: found?.offset,
                precision: found?.config?.timeseriesPrecision
            };
        }
        else {
            return selectedTrend;
        }
    });

    selectedTrendsData?.length && setColor(selectedTrendsData);

    return selectedTrendsData;
};

export const removeSelectedOpTrendKpiData = (selectedTrendDataArrays, unselectedKPI) => {
    const foundIndex = findIndex(selectedTrendDataArrays, (trendDataArray) => trendDataArray?.kpiIdentifier === unselectedKPI.timeseriesKey);
    foundIndex > -1 && selectedTrendDataArrays?.splice(foundIndex, 1);

    return selectedTrendDataArrays;
};

export const removeSelectedTrendKpi = (selectedTrends, unselectedKPI) => {
    const foundIndex = findIndex(selectedTrends, (kpi) => kpi === unselectedKPI.timeseriesKey);
    foundIndex > -1 && selectedTrends.splice(foundIndex, 1);

    return selectedTrends;
};
